import { faPause, faPencil } from "@fortawesome/free-solid-svg-icons"
import { faChevronRight } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Checkbox,
  ChildUser,
  Gender,
  Switch,
  Typography,
} from "@joonapp/web-shared"
import { JoonUIColor } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import ChildPasscodeModal from "./childPasscodeSection/ChildPasscodeModal"
import ChildPasscodeSection from "./childPasscodeSection/ChildPasscodeSection"
import EditChildProfileModal from "./EditChildProfileModal"
import useEditChildProfileModalStore from "./useEditChildProfileModalStore"
import { PREFERENCE_KEY } from "../../../constants"
import {
  useTogglePauseMutation,
  useToggleRoutineTimeLimitsMutation,
} from "../../../networking/families/mutations"
import { ANALYTIC_EVENTS } from "../../../util/analytics"
import { trackAnalyticEvent } from "../../../util/analytics"
import { getGenderLabel } from "../../../util/util"
import useSelectedFamilyMember from "../hooks/useSelectedFamilyMember"
import ManageRoutineTimesModal from "../manageGameplay/ManageRoutineTimesModal"
import { useManageGameplay } from "../manageGameplay/useManageGameplay"
import { useManageRoutineTimesModalStore } from "../manageGameplay/useManageRoutineTimesModalStore"

const SettingsTab = () => {
  const { selectedProfile } = useSelectedFamilyMember()
  const { hasAnyRoutineTimeLimits, updatePreference } = useManageGameplay()
  const togglePauseMutation = useTogglePauseMutation()
  const toggleRoutineTimeLimitsMutation = useToggleRoutineTimeLimitsMutation()
  const { onOpen } = useEditChildProfileModalStore()
  const { onOpen: openRoutineTimesModal } = useManageRoutineTimesModalStore()
  const { t } = useTranslation()
  const child = selectedProfile?.user as ChildUser

  if (!child) return null

  const handleEditChildProfile = () => {
    onOpen(child)
  }

  const handleEditRoutineTimes = () => {
    openRoutineTimesModal(child)
  }

  return (
    <div className="mt-4 flex flex-col gap-6">
      <div className="flex flex-row items-center justify-between border border-borderPrimary rounded-md p-3 bg-white">
        {child.active_doter.is_paused ? (
          <div className="flex flex-row items-center gap-2">
            <FontAwesomeIcon icon={faPause} color={JoonUIColor.icon.accent} />
            <Typography variant="bodyBold">{t("CST_GAME_PAUSED")}</Typography>
          </div>
        ) : (
          <Typography variant="bodyBold">{t("CST_PAUSE_GAME")}</Typography>
        )}
        <Switch
          name="pauseJoon"
          checked={child.active_doter.is_paused}
          onChange={() =>
            togglePauseMutation.mutate({
              doterId: child.active_doter.id,
              isPaused: child.active_doter.is_paused,
            })
          }
          variant="small"
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between">
          <Typography variant="bodyBold">{t("PPS_PROFILE")}</Typography>
          <button onClick={handleEditChildProfile}>
            <div className="flex flex-row items-center gap-1">
              <FontAwesomeIcon
                icon={faPencil}
                color={JoonUIColor.icon.accent}
                fontSize={12}
              />
              <Typography
                variant="bodySmall"
                color={JoonUIColor.semantic.primary}
              >
                {t("QUEST_HEADER_EDIT")}
              </Typography>
            </div>
          </button>
        </div>
        <div className="flex flex-col justify-between items-center border border-borderPrimary rounded-md bg-white">
          <div className="flex flex-row justify-between gap-2 border-b border-borderPrimary p-3 w-full">
            <Typography variant="bodySmall">{t("CST_NAME")}</Typography>
            <Typography variant="bodySmall">{child.name}</Typography>
          </div>
          <div className="flex flex-row justify-between gap-2 p-3 w-full border-b border-borderPrimary">
            <Typography variant="bodySmall">{t("CST_BIRTHDATE")}</Typography>
            <Typography variant="bodySmall">
              {child.birthdate
                ? dayjs(child.birthdate).format("MMM DD, YYYY")
                : t("CST_NA")}
            </Typography>
          </div>
          <div className="flex flex-row justify-between gap-2 p-3 w-full">
            <Typography variant="bodySmall">{t("CST_GENDER")}</Typography>
            <Typography variant="bodySmall">
              {getGenderLabel(child.gender ?? Gender.OTHER)}
            </Typography>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Typography variant="bodyBold">{t("CST_ROUTINE_TIMES")}</Typography>
        <div className="flex flex-col border border-borderPrimary rounded-md bg-white">
          <div className="flex justify-between items-center p-3 border-b border-borderPrimary">
            <Typography variant="bodySmall">{t("CST_MORNING_END")}</Typography>
            <button onClick={handleEditRoutineTimes}>
              <div className="flex items-center gap-2">
                <Typography variant="bodySmall">
                  {child.preferences.afternoon_start_time}
                </Typography>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  color={JoonUIColor.icon.neutral}
                  fontSize={14}
                />
              </div>
            </button>
          </div>
          <div className="flex justify-between items-center p-3 border-b border-borderPrimary">
            <Typography variant="bodySmall">
              {t("CST_AFTERNOON_END")}
            </Typography>
            <button onClick={handleEditRoutineTimes}>
              <div className="flex items-center gap-2">
                <Typography variant="bodySmall">
                  {child.preferences.night_start_time}
                </Typography>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  color={JoonUIColor.icon.neutral}
                  fontSize={14}
                />
              </div>
            </button>
          </div>
          <div className="flex justify-between p-3">
            <div className="flex flex-col">
              <Typography variant="bodySmallBold">
                {t("CST_ONLY_RELEVANT")}
              </Typography>
              <Typography variant="bodySmall">
                {t("CST_ONLY_RELEVANT_DESC")}
              </Typography>
            </div>
            <Switch
              name="showRelevantRoutines"
              checked={child.preferences.hides_future_routines}
              onChange={() =>
                updatePreference(
                  PREFERENCE_KEY.hidesFutureRoutines,
                  !child.preferences.hides_future_routines
                )
              }
              variant="small"
            />
          </div>
        </div>
        <div className="flex flex-col border border-borderPrimary rounded-md p-3 gap-2 bg-white">
          <div className="flex justify-between">
            <Typography variant="bodySmallBold">
              {t("CST_LIMIT_GAMEPLAY")}
            </Typography>
            <Switch
              checked={hasAnyRoutineTimeLimits}
              name="hasAnyRoutineTimeLimits"
              onChange={() =>
                toggleRoutineTimeLimitsMutation.mutate({
                  childId: child.id,
                  value: !hasAnyRoutineTimeLimits,
                })
              }
              variant="small"
            />
          </div>
          <Typography variant="bodySmall">
            {hasAnyRoutineTimeLimits
              ? t("CST_LIMIT_ENABLED_DESC", { name: child.name })
              : t("CST_LIMIT_DISABLED_DESC", { name: child.name })}
          </Typography>

          {hasAnyRoutineTimeLimits && (
            <div className="flex flex-col border border-borderPrimary rounded-md">
              <div className="flex justify-between p-3 border-b border-borderPrimary">
                <Typography variant="bodySmall">
                  {t("CST_MORNING_ROUTINE")}
                </Typography>
                <Checkbox
                  name="morningRoutine"
                  selected={child.preferences.morning_limit}
                  onChange={() => {
                    updatePreference(
                      PREFERENCE_KEY.morningLimit,
                      !child.preferences.morning_limit
                    )
                  }}
                  hideBorder
                />
              </div>
              <div className="flex justify-between p-3 border-b border-borderPrimary">
                <Typography variant="bodySmall">
                  {t("CST_AFTERNOON_ROUTINE")}
                </Typography>
                <Checkbox
                  name="afternoonRoutine"
                  selected={child.preferences.afternoon_limit}
                  onChange={() => {
                    updatePreference(
                      PREFERENCE_KEY.afternoonLimit,
                      !child.preferences.afternoon_limit
                    )
                  }}
                  hideBorder
                />
              </div>
              <div className="flex justify-between p-3">
                <Typography variant="bodySmall">
                  {t("CST_NIGHT_ROUTINE")}
                </Typography>
                <Checkbox
                  name="nightRoutine"
                  selected={child.preferences.night_limit}
                  onChange={() => {
                    updatePreference(
                      PREFERENCE_KEY.nightLimit,
                      !child.preferences.night_limit
                    )
                  }}
                  hideBorder
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Typography variant="bodyBold">{t("CST_GAMEPLAY_SETTINGS")}</Typography>
        <div className="flex flex-col border border-borderPrimary rounded-md p-3 gap-2 bg-white">
          <div className="flex justify-between">
            <Typography variant="bodySmallBold">
              {t("DC_ENABLE_TITLE")}
            </Typography>
            <Switch
              name="enableDoterConversations"
              checked={child.preferences.enable_doter_chat}
              onChange={() => {
                trackAnalyticEvent(ANALYTIC_EVENTS.TOGGLE_DOTER_CHAT, {
                  source: "settings",
                  enabled: !child.preferences.enable_doter_chat,
                })
                updatePreference(
                  PREFERENCE_KEY.enableDoterChat,
                  !child.preferences.enable_doter_chat
                )
              }}
              variant="small"
            />
          </div>
          {child.preferences.enable_doter_chat ? (
            <Typography variant="bodySmall">
              {t("DC_SETTINGS_ENABLED_DESC", { name: child.name })}
            </Typography>
          ) : (
            <Typography variant="bodySmall">
              {t("DC_SETTINGS_DISABLED_DESC", { name: child.name })}
            </Typography>
          )}
        </div>
        <div className="flex flex-col border border-borderPrimary rounded-md p-3 gap-2 bg-white">
          <div className="flex justify-between">
            <Typography variant="bodySmallBold">
              {t("CST_SELF_QUESTS")}
            </Typography>
            <Switch
              name="enableDoterWishes"
              checked={child.preferences.enable_doter_wishes}
              onChange={() =>
                updatePreference(
                  PREFERENCE_KEY.enableDoterWishes,
                  !child.preferences.enable_doter_wishes
                )
              }
              variant="small"
            />
          </div>
          {child.preferences.enable_doter_wishes ? (
            <Typography variant="bodySmall">
              {t("CST_SELF_QUESTS_ENABLED", { name: child.name })}
            </Typography>
          ) : (
            <Typography variant="bodySmall">
              {t("CST_SELF_QUESTS_DISABLED", { name: child.name })}
            </Typography>
          )}
        </div>
      </div>
      <ChildPasscodeSection />
      <ChildPasscodeModal />
      <EditChildProfileModal />
      <ManageRoutineTimesModal />
    </div>
  )
}

export default SettingsTab
